import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Trash2, Radar } from "lucide-react";
import { toast } from "sonner";
import { adminAivisList, adminAivisAdd, adminAivisDelete } from "@/lib/api";

const TYPES = ["verified", "observed", "simulated", "estimate"];
const STATUSES = ["cited", "mentioned", "absent"];
const ENGINES = ["ChatGPT", "Perplexity", "Google AI Overviews", "Gemini", "Bing Copilot", "Claude", "Meta AI"];
const input = "w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-brand outline-none";
const label = "text-xs font-semibold text-slate-600 block mb-1";

const TYPE_TONE = {
  verified: "bg-emerald-50 text-emerald-700", observed: "bg-blue-50 text-brand",
  simulated: "bg-amber-50 text-amber-700", estimate: "bg-slate-100 text-slate-500",
};
const STATUS_TONE = {
  cited: "bg-emerald-50 text-emerald-700", mentioned: "bg-blue-50 text-brand", absent: "bg-rose-50 text-rose-600",
};

const BLANK = { engine: "ChatGPT", query: "", type: "observed", status: "mentioned", note: "", source_url: "" };

export default function AdminAiVisibility() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [adding, setAdding] = useState(false);

  const load = () => adminAivisList().then(setData).catch(() => toast.error("Failed to load — admin login required"));
  useEffect(() => { load(); }, []);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const add = async () => {
    if (!form.query.trim()) { toast.error("Enter the search query / prompt"); return; }
    setAdding(true);
    try {
      await adminAivisAdd({ ...form, query: form.query.trim(), note: form.note.trim(), source_url: form.source_url.trim() });
      setForm(BLANK);
      toast.success("Observation added — live on /ai-visibility");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setAdding(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this observation?")) return;
    try { await adminAivisDelete(id); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  if (!data) return <main className="min-h-screen bg-slate-50 grid place-items-center"><Loader2 className="animate-spin text-brand" size={26} /></main>;

  const s = data.summary || {};
  return (
    <main className="min-h-screen bg-slate-50 pb-24" data-testid="admin-ai-visibility">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/admin" className="text-slate-500 hover:text-brand text-sm flex items-center gap-1.5 mb-3"><ArrowLeft size={14} /> Back to console</Link>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2"><Radar size={22} className="text-brand" /><h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">AI-Search Visibility</h1></div>
          <a href="/ai-visibility" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-500 hover:text-brand px-2">Preview ↗</a>
        </div>
        <p className="text-sm text-slate-500 mt-2">Record how the brand appears across AI answer engines. Always label each observation honestly by certainty — never overstate.</p>

        {/* summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          {[["Cited", s.cited, "text-emerald-600"], ["Mentioned", s.mentioned, "text-brand"], ["Not found", s.absent, "text-rose-500"], ["Total", s.total, "text-slate-900"]].map(([l, v, c]) => (
            <div key={l} className="bg-white border border-slate-200 rounded-xl p-4 card-shadow">
              <div className={`text-2xl font-bold ${c}`}>{v ?? 0}</div>
              <div className="text-[11px] text-slate-500 uppercase tracking-wide mt-1">{l}</div>
            </div>
          ))}
        </div>

        {/* add form */}
        <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-5 mt-6" data-testid="aivis-add-form">
          <div className="text-sm font-semibold text-slate-800 mb-3">Add observation</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <span className={label}>Engine</span>
              <select value={form.engine} onChange={(e) => set({ engine: e.target.value })} className={input} data-testid="aivis-engine">
                {ENGINES.map((x) => <option key={x}>{x}</option>)}
              </select>
            </div>
            <div>
              <span className={label}>Query / prompt tested</span>
              <input value={form.query} onChange={(e) => set({ query: e.target.value })} placeholder="e.g. best website growth agency" className={input} data-testid="aivis-query" />
            </div>
            <div>
              <span className={label}>Certainty type</span>
              <select value={form.type} onChange={(e) => set({ type: e.target.value })} className={input} data-testid="aivis-type">
                {TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </div>
            <div>
              <span className={label}>Status</span>
              <select value={form.status} onChange={(e) => set({ status: e.target.value })} className={input} data-testid="aivis-status">
                {STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <span className={label}>Note</span>
              <input value={form.note} onChange={(e) => set({ note: e.target.value })} placeholder="How it was observed / any caveat" className={input} data-testid="aivis-note" />
            </div>
            <div className="sm:col-span-2">
              <span className={label}>Source URL (optional)</span>
              <input value={form.source_url} onChange={(e) => set({ source_url: e.target.value })} placeholder="https://…" className={input} data-testid="aivis-source" />
            </div>
          </div>
          <button onClick={add} disabled={adding} data-testid="aivis-add-btn"
            className="mt-4 inline-flex items-center gap-1.5 bg-brand text-white rounded-lg px-5 py-2.5 text-sm font-semibold disabled:opacity-60">
            {adding ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Add observation
          </button>
        </div>

        {/* list */}
        <div className="space-y-2 mt-6" data-testid="aivis-list">
          {(data.observations || []).map((o) => (
            <div key={o.id} className="bg-white border border-slate-200 rounded-xl card-shadow p-4 flex items-start gap-3" data-testid={`aivis-item-${o.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-slate-800">{o.engine}</span>
                  <span className={`text-[10px] font-bold uppercase rounded-full px-2 py-0.5 ${TYPE_TONE[o.type] || "bg-slate-100 text-slate-500"}`}>{o.type}</span>
                  <span className={`text-[10px] font-bold uppercase rounded-full px-2 py-0.5 ${STATUS_TONE[o.status] || "bg-slate-100"}`}>{o.status}</span>
                </div>
                <div className="text-sm text-slate-600 mt-1">“{o.query}”</div>
                {o.note && <div className="text-[12px] text-slate-400 mt-0.5">{o.note}</div>}
                {o.source_url && <a href={o.source_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-brand hover:underline">{o.source_url}</a>}
              </div>
              <button onClick={() => del(o.id)} data-testid={`aivis-del-${o.id}`} className="text-slate-300 hover:text-red-500 p-1.5 shrink-0"><Trash2 size={16} /></button>
            </div>
          ))}
          {(data.observations || []).length === 0 && <div className="text-sm text-slate-400 text-center py-8">No observations yet.</div>}
        </div>
      </div>
    </main>
  );
}
